import React from "react";
import cartBorder from "@assets/images/cartBorder.webp";
import summaryBorder from "@assets/images/cartsummaryBorder.webp";
import shippingBorder from "@assets/images/borders/shippingBorder.webp";
import { currencyFormatter } from "@src/utils/CardValidationHelper";
import { NavLink } from "react-router-dom";
import routes from "@src/routes/routes";
import type { ICartSummarySection } from "@src/types/AppTypes";

const CartSummarySection: React.FC<ICartSummarySection> = ({
  isLoading,
  cartItems,
  checkIfAllItemsInStock,
  coupon,
  displayOrAddShipping,
  selectedShippingId,
  setSelectedShippingId,
  setShowClearCart,
  shippingOptions,
  getCartItemsPrice,
  taxValue,
  totalCartItems,
  getTotalCartValue,
}) => {
  return (
    cartItems.length !== 0 && (
      <div className="p-10 max-xl:p-4 max-lg:p-12 sticky top-0 max-md:p-0">
        <img
          className="absolute top-0 left-0 size-full max-md:hidden"
          src={cartBorder}
          alt=""
        />
        <div className="relative z-10 flex flex-col gap-4 max-sm:gap-3">
          <div className="flex items-center flex-wrap justify-between max-xl:flex-col max-lg:flex-row">
            <h5 className="text-primary text-2xl max-sm:text-xl">
              Cart Summary
            </h5>
            <button
              className="flex text-lg text-primary-gradient items-center gap-1 duration-150 hover:scale-95 max-md:hidden"
              onClick={() => {
                setShowClearCart(true);
              }}
            >
              <i className="ri-delete-bin-7-fill"></i>
              Clear Cart
            </button>
          </div>
          <div className="flex items-center justify-between max-md:hidden">
            <h6 className="text-primary text-xl max-sm:text-lg">Items</h6>
            <span className="text-primary text-xl max-sm:text-lg">
              {totalCartItems}
            </span>
          </div>
          {displayOrAddShipping && (
            <>
              <h5 className="text-primary text-xl max-sm:text-lg">
                Shipping methods
              </h5>
              <div className="flex flex-col gap-5 mb-3 max-sm:mt-2 max-h-52 max-md:max-h-max overflow-auto">
                {shippingOptions?.map((item) => {
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between relative py-4 px-6 cursor-pointer duration-150 hover:scale-95 ${
                        selectedShippingId !== item.id && "opacity-50"
                      }`}
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedShippingId({
                          type: "setSelectedShipping",
                          payload: item.id,
                        });
                      }}
                      aria-label="Shipping Price"
                    >
                      <img
                        className="absolute top-0 left-0 size-full"
                        src={shippingBorder}
                        alt=""
                      />
                      <h6 className="text-primary text-2xl max-sm:text-xl">
                        {item.title}
                      </h6>
                      <span className="text-primary text-2xl max-sm:text-xl">
                        {currencyFormatter(Number(item.price))}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div className="flex flex-col gap-5 max-md:gap-3">
            <div className="flex items-center justify-between">
              <h6 className="text-primary text-xl max-sm:text-lg">Subtotal</h6>
              <span className="text-primary text-xl max-sm:text-lg">
                {currencyFormatter(Number(getCartItemsPrice ?? "0"))}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <h6 className="text-primary text-xl max-sm:text-lg">Discount</h6>
              <span className="text-primary text-xl max-md:text-lg">
                {coupon.isApplied && "- "}
                {currencyFormatter(Number(coupon.discount ?? "0"))}
              </span>
            </div>
            {displayOrAddShipping && (
              <div className="flex items-center justify-between">
                <h6 className="text-primary text-xl max-sm:text-lg">
                  shipping
                </h6>
                <span className="text-primary text-xl max-sm:text-lg">
                  {selectedShippingId
                    ? currencyFormatter(
                        Number(
                          shippingOptions.filter(
                            (val) => val.id === selectedShippingId
                          )[0].price ?? 0
                        )
                      )
                    : currencyFormatter(0)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <h6 className="text-primary text-xl max-sm:text-lg">
                Tax {" (10%)"}
              </h6>
              <span className="text-primary text-xl max-sm:text-lg">
                {currencyFormatter(taxValue)}
              </span>
            </div>
          </div>
          <img className="w-full" src={summaryBorder} alt="" />
          <div className="flex items-center justify-between mb-5 max-sm:mb-2">
            <h6 className="text-primary text-2xl max-sm:text-xl">Total</h6>
            <span className="text-primary text-2xl max-sm:text-xl">
              {getTotalCartValue}
            </span>
          </div>
          <div className="text-center px-4 py-0 flex flex-col items-center gap-2">
            <button
              className="flex justify-center text-2xl w-full max-w-sm mx-auto p-[1.2rem] bg-100 bg-no-repeat bg-[url('@assets/images/btnBgImage.webp')] text-black duration-150 hover:scale-95 disabled:opacity-50 disabled:hover:cursor-not-allowed max-md:text-2xl max-sm:text-xl"
              onClick={() => {
                if (isLoading) return;
                checkIfAllItemsInStock();
              }}
              disabled={cartItems.length === 0 || isLoading}
              aria-label="Checkout"
            >
              {isLoading ? (
                <i className="ri-loop-right-line btn-loader"></i>
              ) : (
                "Check Out"
              )}
            </button>

            <NavLink
              className="p-3 text-primary-gradient text-xl duration-150 hover:scale-95 inline-flex"
              to={routes.shop}
            >
              Continue Shopping
            </NavLink>
          </div>
        </div>
      </div>
    )
  );
};

export default React.memo(CartSummarySection);
